import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_HOMEOWNER_BIO } from "../graphql/homeowner-bio";
import { useUserContext } from "./userContext";

type HomeownerBioQueryData = {
  getHomeOwnerBio?: {
    image?: {
      url?: string | null;
    } | null;
  } | null;
};

type HomeownerBioQueryVars = {
  homeOwnerId: string;
};

type UseHomeownerAvatarOptions = {
  autoLoad?: boolean;
};

export const useHomeownerAvatar = (options?: UseHomeownerAvatarOptions) => {
  const autoLoad = options?.autoLoad ?? true;
  const { user } = useUserContext();
  const [getHomeownerBioQuery, { data }] = useLazyQuery<
    HomeownerBioQueryData,
    HomeownerBioQueryVars
  >(GET_HOMEOWNER_BIO);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  const updateAvatarState = useCallback((url?: string) => {
    setAvatarUrl(url);
    if (typeof window === "undefined") {
      return;
    }

    if (url) {
      localStorage.setItem("homeownerAvatarUrl", url);
    } else {
      localStorage.removeItem("homeownerAvatarUrl");
    }

    window.dispatchEvent(
      new CustomEvent("homeowner-avatar-updated", { detail: url ?? null })
    );
  }, []);

  const initials = useMemo(() => {
    const first = user?.firstName?.trim()?.[0] ?? "";
    const last = user?.lastName?.trim()?.[0] ?? "";
    const combined = `${first}${last}`.trim();
    return combined.length ? combined.toUpperCase() : undefined;
  }, [user?.firstName, user?.lastName]);

  const refreshAvatar = useCallback(
    async (forceNetwork: boolean = true) => {
      if (user?.accountType !== "Homeowner" || !user?.id) {
        setAvatarUrl(undefined);
        return undefined;
      }

      try {
        const result = await getHomeownerBioQuery({
          variables: { homeOwnerId: user.id },
          fetchPolicy: forceNetwork ? "network-only" : "cache-first",
        });
        const url =
          (result?.data?.getHomeOwnerBio?.image?.url as string | undefined) ??
          undefined;
        updateAvatarState(url);
        return url;
      } catch (error) {
        console.error("Failed to refresh homeowner avatar:", error);
        return undefined;
      }
    },
    [getHomeownerBioQuery, updateAvatarState, user?.accountType, user?.id]
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedUrl = localStorage.getItem("homeownerAvatarUrl");
      if (storedUrl && storedUrl.trim() !== "") {
        setAvatarUrl(storedUrl);
      }

      const handleAvatarUpdated = (event: Event) => {
        const detail = (event as CustomEvent<string | null>).detail;
        setAvatarUrl(detail ?? undefined);
      };

      window.addEventListener(
        "homeowner-avatar-updated",
        handleAvatarUpdated as EventListener
      );

      return () => {
        window.removeEventListener(
          "homeowner-avatar-updated",
          handleAvatarUpdated as EventListener
        );
      };
    }
  }, []);

  useEffect(() => {
    if (user?.accountType !== "Homeowner" || !user?.id) {
      updateAvatarState(undefined);
      return;
    }

    const cachedUrl = data?.getHomeOwnerBio?.image?.url as string | undefined;
    if (cachedUrl) {
      updateAvatarState(cachedUrl);
      return;
    }

    if (autoLoad) {
      refreshAvatar(true);
    }
  }, [
    autoLoad,
    data?.getHomeOwnerBio?.image?.url,
    refreshAvatar,
    updateAvatarState,
    user?.accountType,
    user?.id,
  ]);

  return { avatarUrl, initials, refreshAvatar };
};
